import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PanelCard,
  SelectField,
  SliderField,
  StatusPill,
  TextField,
  ToggleField,
} from './panelUi'
import {
  DEFAULT_USER_SETTINGS,
  getSetting,
  isUsingBundledTmdbApiKey,
  type UserSettings,
} from './useUserSettings'
import { useWriteSettings } from './useWriteSettings'
import { useToast } from '../ui'

type ValidationState = 'idle' | 'loading' | 'valid' | 'invalid'

/**
 * PWA TMDB settings — mirrors src/renderer/components/settings/TMDBSettingsPanel.tsx
 * but drops the desktop-only bits (rename, posters/backdrops download,
 * naming pattern, poster quality, load-from-file). The API key validates
 * against `/3/configuration` with a 300ms debounce.
 */
export function TMDBSettingsPanel({
  settings,
}: {
  settings: UserSettings | null
}) {
  const { updateSetting } = useWriteSettings()
  const { showSuccess, showError } = useToast()
  const [showKey, setShowKey] = useState(false)
  const [keyDraft, setKeyDraft] = useState('')
  const [validation, setValidation] = useState<ValidationState>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)

  // Keep the draft in sync with Firestore so external writes refresh the field.
  useEffect(() => {
    setKeyDraft(settings?.tmdbApiKey ?? '')
  }, [settings?.tmdbApiKey])

  // Debounced client-side validation against TMDB /3/configuration.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const key = keyDraft.trim()
    if (!key) {
      setValidation('idle')
      setValidationError(null)
      return
    }
    setValidation('loading')
    setValidationError(null)
    debounceRef.current = setTimeout(async () => {
      try {
        // TMDB supports both the v3 api_key query param and v4 Bearer tokens.
        // v4 tokens start with "eyJ" (JWT), v3 keys are 32-char hex.
        const isBearer = key.startsWith('eyJ')
        const url = isBearer
          ? 'https://api.themoviedb.org/3/configuration'
          : `https://api.themoviedb.org/3/configuration?api_key=${encodeURIComponent(key)}`
        const res = await fetch(url, {
          headers: isBearer ? { Authorization: `Bearer ${key}` } : undefined,
        })
        if (!res.ok) {
          setValidation('invalid')
          setValidationError(`TMDB rejected the key (HTTP ${res.status})`)
          return
        }
        const data = await res.json()
        if (data?.images?.base_url) {
          setValidation('valid')
          setValidationError(null)
        } else {
          setValidation('invalid')
          setValidationError('Unexpected response from TMDB')
        }
      } catch (err) {
        setValidation('invalid')
        setValidationError((err as Error).message)
      }
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [keyDraft])

  const savedKey = settings?.tmdbApiKey ?? ''
  const dirty = keyDraft !== savedKey
  const canSave =
    dirty && (keyDraft.trim() === '' || validation === 'valid')
  // True when the user hasn't supplied their own key but the build ships
  // with a developer-bundled fallback. In that case the app is already
  // working — the API key field is optional, not a setup step.
  const usingBundledKey = isUsingBundledTmdbApiKey(settings)

  const statusPill = useMemo(() => {
    if (validation === 'loading') return <StatusPill kind="loading" label="Checking…" />
    if (validation === 'valid') return <StatusPill kind="ok" label="Valid" />
    if (validation === 'invalid')
      return <StatusPill kind="error" label="Invalid" />
    if (savedKey) return <StatusPill kind="ok" label="Connected" />
    if (usingBundledKey)
      return <StatusPill kind="ok" label="Connected (bundled)" />
    return <StatusPill kind="idle" label="Not set" />
  }, [validation, savedKey, usingBundledKey])

  const lang = getSetting(settings, 'tmdbPreferredLanguage')
  const adult = getSetting(settings, 'tmdbIncludeAdultContent')
  const autoImport = getSetting(settings, 'tmdbAutoImportEnabled')
  const searchConf = getSetting(settings, 'tmdbSearchConfidenceThreshold')
  const autoMatch = getSetting(settings, 'tmdbAutoMatchThreshold')

  return (
    <PanelCard
      title="TMDB"
      description="Posters, search, recommendations, and details."
      status={statusPill}
    >
      <TextField
        label={usingBundledKey ? 'TMDB API key (optional)' : 'TMDB API key'}
        type={showKey ? 'text' : 'password'}
        value={keyDraft}
        placeholder={
          usingBundledKey
            ? 'Optional — leave blank to use the bundled key'
            : 'v3 key or v4 read access token'
        }
        onChange={setKeyDraft}
        rightSlot={
          <>
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="rounded-md border border-border-primary bg-bg-tertiary px-2 text-[11px] text-text-secondary hover:bg-bg-primary"
              title={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
            <button
              type="button"
              disabled={!canSave}
              onClick={async () => {
                try {
                  const trimmed = keyDraft.trim()
                  await updateSetting(
                    'tmdbApiKey',
                    trimmed ? trimmed : null
                  )
                  if (trimmed) {
                    showSuccess('TMDB connected')
                  } else {
                    showSuccess('TMDB key cleared')
                  }
                } catch (err) {
                  console.error('[TMDBSettingsPanel] save failed', err)
                  showError('Failed to save TMDB key')
                }
              }}
              className="rounded-md bg-accent-primary px-2.5 text-[11px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Save
            </button>
          </>
        }
        description={
          <>
            {usingBundledKey ? (
              <>
                TMDB is already connected with a bundled API key — no setup
                needed. Supply your own key only if you want to use your
                personal TMDB rate limits.{' '}
              </>
            ) : (
              <>
                Get a free key at{' '}
                <a
                  href="https://www.themoviedb.org/settings/api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-primary underline"
                >
                  themoviedb.org
                </a>
                .{' '}
              </>
            )}
            {validationError && (
              <span className="text-red-400">{validationError}</span>
            )}
          </>
        }
      />

      <SelectField
        label="Preferred language"
        value={lang ?? DEFAULT_USER_SETTINGS.tmdbPreferredLanguage}
        options={[
          { value: 'en', label: 'English' },
          { value: 'es', label: 'Spanish' },
          { value: 'fr', label: 'French' },
          { value: 'de', label: 'German' },
          { value: 'it', label: 'Italian' },
          { value: 'pt', label: 'Portuguese' },
          { value: 'ja', label: 'Japanese' },
          { value: 'ko', label: 'Korean' },
          { value: 'zh', label: 'Chinese' },
          { value: 'ru', label: 'Russian' },
          { value: 'ar', label: 'Arabic' },
          { value: 'hi', label: 'Hindi' },
          { value: 'pl', label: 'Polish' },
        ]}
        onChange={(v) => updateSetting('tmdbPreferredLanguage', v)}
      />

      <ToggleField
        label="Include adult content"
        description="Include adult/mature content in search results"
        checked={Boolean(adult)}
        onChange={(v) => updateSetting('tmdbIncludeAdultContent', v)}
      />

      <ToggleField
        label="Auto-import metadata"
        description="Automatically pull metadata for new ratings"
        checked={Boolean(autoImport)}
        onChange={(v) => updateSetting('tmdbAutoImportEnabled', v)}
      />

      <SliderField
        label="Search confidence threshold"
        description="Minimum confidence score for search results"
        value={searchConf ?? DEFAULT_USER_SETTINGS.tmdbSearchConfidenceThreshold}
        min={0}
        max={1}
        step={0.05}
        onChange={(v) => updateSetting('tmdbSearchConfidenceThreshold', v)}
      />

      <SliderField
        label="Auto-match threshold"
        description="Minimum confidence score for automatic matching"
        value={autoMatch ?? DEFAULT_USER_SETTINGS.tmdbAutoMatchThreshold}
        min={0}
        max={1}
        step={0.05}
        onChange={(v) => updateSetting('tmdbAutoMatchThreshold', v)}
      />
    </PanelCard>
  )
}
